/**
 * The assistant that answers on the team planning: shifts, unavailability,
 * staffing, appointment slots.
 *
 * Named after the planning and not after human resources on purpose: it
 * reasons on skills, contracted hours and availability, never on a person.
 */
export const PLANNING_ASSISTANT = 'planning';

/**
 * The group of assistant parameters that shapes a health journey: how acts
 * are spaced, which of them may share a visit, and how long such a visit may
 * run. They are declared by the same assistant as the planning ones, and
 * edited from the health journey screen rather than from the copilot panel —
 * beside the plan they shape.
 */
export const HEALTH_JOURNEY_GROUP = 'health_journey';

/**
 * How many past turns travel with a question.
 *
 * Nothing is archived server-side, so the whole conversation is re-sent on
 * every turn and its length is a cost paid again each time. The cap is
 * enforced here as well as by the server: a conversation that outgrows it
 * keeps answering on its recent turns rather than being refused.
 */
export const MAX_HISTORY_TURNS = 10;

/**
 * Characters a question may carry, mirrored from the server's own bound.
 *
 * Held here so the field stops at the limit rather than letting the turn be
 * refused: a 422 read as « reformulez votre demande » would say nothing about
 * the only thing that was wrong, which is the length.
 */
export const MAX_PROMPT_CHARACTERS = 2000;

/** Constraint kinds the server accepts, mirrored from its closed set. */
export const NO_ACT_ON_WEEKDAY = 'no_act_on_weekday';

export const BUFFER_BEFORE_CLOSING = 'buffer_before_closing';

/** ISO-8601 weekdays, as the constraints number them. */
export const WEEKDAYS = [
    {value: 1, label: 'lundi'},
    {value: 2, label: 'mardi'},
    {value: 3, label: 'mercredi'},
    {value: 4, label: 'jeudi'},
    {value: 5, label: 'vendredi'},
    {value: 6, label: 'samedi'},
    {value: 7, label: 'dimanche'},
];

/**
 * Examples of what the pharmacist could ask, cycled through the composer's
 * placeholder.
 *
 * They used to sit as buttons under the field, which made the assistant look
 * limited to four options — « je me sens cloisonné parmi ces quatre trucs ».
 * As placeholders they invite a question in the pharmacist's own words instead
 * of offering a menu.
 */
export const QUESTION_EXAMPLES = [
    'Micheline part trois semaines en septembre, comment je couvre ?',
    'Réorganiser mon planning, Martine est absente jeudi',
    'Trouver un créneau calme pour un rendez-vous laboratoire',
    'Claire est à 32 h au lieu de 35, comment je rattrape ?',
];

/**
 * Routes where the assistant is offered.
 *
 * It answers on the team planning, so it is only worth offering where the
 * pharmacist is looking at it. Both the launcher and the panel read this list,
 * so they can never disagree on where the assistant exists.
 */
export const ASSISTANT_ROUTES = ['agenda', 'agenda.health-journey', 'planning', 'team'];

/**
 * What each conformity verdict says, in the pharmacist's terms.
 *
 * `alerte` names something to weigh, not a refusal: the rules admit
 * derogations and agreements the module knows nothing about, and the
 * pharmacist decides. `non_verifiable` is deliberately not reassuring either —
 * no rule could be evaluated, which is a different answer from « rien à
 * signaler ».
 */
export const CONFORMITY_LABELS: Record<string, string> = {
    compliant: 'Conforme aux règles vérifiées',
    alert: 'Point de vigilance',
    non_compliant: 'Exercice impossible en l’état',
    unverifiable: 'Conformité non vérifiable'
};

/**
 * The only severity that belongs under the fold.
 *
 * Listed rather than negated so that anything this build cannot grade — a
 * value frozen before a rename, a level the API adds later — is surfaced
 * rather than buried. The server takes the same side: an unparseable severity
 * is read as the strictest one it knows.
 */
export const FOLDED_SEVERITIES = ['information'];

/**
 * Operations the pharmacist carries out themselves.
 *
 * The system never writes them — an appointment lives on the health server,
 * and a proposition that claimed to have moved it would be lying.
 */
export const MANUAL_OPERATION_LABELS: Record<string, string> = {
    REASSIGN_APPOINTMENT: 'Rendez-vous à réaffecter dans l’agenda'
};

/** How sure a hypothesis is, when the decision layer graded it. */
export const RELIABILITY_LABELS: Record<string, string> = {
    haute: 'fiabilité haute',
    moyenne: 'fiabilité moyenne',
    faible: 'fiabilité faible'
};

/**
 * Where a scenario stands, said to the pharmacist rather than to the system.
 *
 * `brouillon` and `proposee` are both « Proposé »: nothing transitions between
 * them, so an undecided scenario sits in the first while having been put to
 * the pharmacist just the same. Every state is named rather than left to a
 * fallback — a label that guesses would assert something about a record a
 * contestation is answered with.
 */
export const PROPOSITION_STATE_LABELS: Record<string, string> = {
    draft: 'Proposé',
    proposed: 'Proposé',
    applied: 'Appliqué',
    rejected: 'Écarté',
    cancelled: 'Annulé',
    expired: 'Périmé'
};

/** What each kind of change does to a day, in the diff. */
export const DIFF_KIND_LABELS: Record<string, string> = {
    ajoute: 'Journée ajoutée',
    deplace: 'Horaires modifiés',
    retire: 'Journée retirée'
};

/**
 * What the pharmacist can do about each refusal.
 *
 * A planning that moved is the frequent case, not the exceptional one: two
 * people often work in parallel in an officine, and a button that fails
 * without saying why is what destroys confidence in the whole panel.
 */
export const DECISION_ERRORS: Record<number, string> = {
    409: 'Le planning a changé depuis le calcul de ce scénario : il ne peut plus être appliqué tel quel. '
        + 'Reposez votre question pour en obtenir un à jour.',
    422: 'Ce scénario ne contient aucune modification que l’application puisse écrire : '
        + 'les réaffectations de rendez-vous se font depuis l’agenda.'
};

export const DECISION_GENERIC_ERROR = "La décision n'a pas pu être enregistrée — réessayez.";

/**
 * Undoing rewrites the planning, unlike rejecting a proposition, which writes
 * nothing. So it is confirmed, and the wording says what will happen rather
 * than asking « êtes-vous sûr ».
 */
export const CANCEL_CONFIRMATION =
    'Les horaires modifiés par ce scénario seront rétablis tels qu’ils étaient avant son application.';

export const CANCEL_ERRORS: Record<number, string> = {
    409: 'Le planning a changé depuis l’application : rétablir les anciens horaires écraserait ces '
        + 'modifications. Ajustez-les depuis l’écran Planning.'
};
